import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
    ExternalLink,
    KeyRound,
    Loader2,
    LogOut,
    RotateCw,
    ShieldAlert,
    ShieldOff,
    type LucideIcon
} from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { z } from 'zod'

import PacksLogo from '@/assets/Packs.svg?react'
import { isAdmitted, useAuth } from '@/auth/auth-context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api-client'
import { ApiError, getApiErrorMessage } from '@/lib/api-error'
import { env } from '@/lib/env'
import { queryKeys } from '@/lib/query-keys'
import { cn } from '@/lib/utils'
import { ThemeToggle } from '@/theme/theme-toggle'
import { UserRole } from '@/types/user'

const searchSchema = z.object({
    redirect: z.string().optional()
})

export const Route = createFileRoute('/login')({
    validateSearch: searchSchema,
    component: NoAccessPage
})

/**
 * There is no sign-in form here, and there cannot be one.
 *
 * The session is a cookie set on the parent domain by the storefront's login, and
 * every admin request rides on it via `credentials: 'include'`. A form on this
 * origin would post to the same `/auth/login` and land the operator in exactly the
 * same place, so the legacy page's email and password fields were decoration over a
 * flow that had already happened somewhere else. The code field on this page does
 * not sign anyone in either: it verifies a session that already exists, for a
 * network where a 2FA code has not been entered yet.
 *
 * What is actually useful at this point is naming which problem the operator has,
 * because they have different fixes: no session at all, a session belonging to an
 * account without the Admin role, an admin who still has to turn 2FA on, or a
 * session this network has not verified. Each one is a different `tone` below, so
 * the status reads before the prose does.
 */
function NoAccessPage() {
    const { redirect } = Route.useSearch()
    const { user, isReady, isAuthenticated, requires2fa, signOut } = useAuth()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const host = hostname(env.siteUrl)
    const admitted = isAdmitted(user, isAuthenticated)

    // A session the console can admit that reaches this page came from a stale
    // bookmark or a back button, not from a failed check. Send it where it was
    // going. Same predicate as AuthGate, or the two redirect each other forever.
    useEffect(() => {
        if (isReady && admitted) {
            void navigate({ to: redirect ?? '/', replace: true })
        }
    }, [isReady, admitted, navigate, redirect])

    // Signing in, and turning 2FA on, happen in another tab, so the answer changes
    // without anything here being clicked. Re-probing is the whole recovery path.
    const checkAgain = () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.session })
    }

    if (!isReady) return <CheckingSession />

    // Admitted sessions are on their way into the app. Rendering a denial under
    // that redirect is the wrong screen for a frame.
    if (admitted) return null

    if (requires2fa) {
        return (
            <Shell redirect={redirect}>
                <NetworkCode host={host} onCheckAgain={checkAgain} />
            </Shell>
        )
    }

    // Every admitted admin already returned. An authenticated admin who remains
    // has not turned 2FA on. The probe still succeeds for them; `/secure/admin` does not.
    if (user && isAuthenticated && user.role === UserRole.Admin) {
        return (
            <Shell redirect={redirect}>
                <Gate
                    tone="warning"
                    icon={ShieldAlert}
                    status="Setup required"
                    title="Turn on two-factor authentication"
                    actions={
                        <>
                            <Action href={`${env.siteUrl}/profile/security`}>Security settings</Action>
                            <CheckAgain onClick={checkAgain} />
                        </>
                    }>
                    <Account email={user.email} />
                    <p>
                        This console is only open to accounts with two-factor authentication, and it is off for yours.
                        Turn it on at {host}, then come back to this tab.
                    </p>
                </Gate>
            </Shell>
        )
    }

    const signedInElsewhere = isAuthenticated && user?.role !== UserRole.Admin

    if (signedInElsewhere) {
        return (
            <Shell redirect={redirect}>
                <Gate
                    tone="danger"
                    icon={ShieldOff}
                    status="Not an administrator"
                    title="This account cannot open the console"
                    actions={
                        <>
                            <Button variant="outline" className="w-full sm:w-auto" onClick={() => void signOut()}>
                                <LogOut />
                                Sign out
                            </Button>
                            <CheckAgain onClick={checkAgain} />
                        </>
                    }>
                    <Account email={user?.email} />
                    <p>
                        The account is signed in, but it does not hold the Admin role, so every admin endpoint will
                        refuse it.
                    </p>
                    <p>
                        Ask whoever administers Packs to grant the role. If your staff account is a different one, sign
                        out and sign back in with that.
                    </p>
                </Gate>
            </Shell>
        )
    }

    return (
        <Shell redirect={redirect}>
            <Gate
                tone="neutral"
                icon={KeyRound}
                status="Not signed in"
                title={`Sign in on ${host} to continue`}
                actions={
                    <>
                        <Action href={env.siteUrl}>Sign in on {host}</Action>
                        <CheckAgain onClick={checkAgain} />
                    </>
                }>
                <p>
                    The console reads the session the storefront sets, so signing in happens there rather than here.
                    Sign in with your staff account, then come back to this tab and check again.
                </p>
                <p>
                    If you are already signed in on {host} and still see this, the account is missing the Admin role and
                    someone with access has to grant it.
                </p>
            </Gate>
        </Shell>
    )
}

function NetworkCode({ host, onCheckAgain }: { host: string; onCheckAgain: () => void }) {
    const queryClient = useQueryClient()
    const [code, setCode] = useState('')
    const [message, setMessage] = useState<string | null>(null)
    const [pending, setPending] = useState(false)

    const verify = async (value: string) => {
        if (value.length !== 6 || pending) return

        setPending(true)
        setMessage(null)
        try {
            const result = await api.post<{ success: boolean; message?: string }>(
                '/auth/two-factor',
                { code: value },
                { skipUnauthorizedHandler: true }
            )
            if (result.success) {
                await queryClient.invalidateQueries({ queryKey: queryKeys.session })
                return
            }
            setMessage(result.message?.trim() || 'That code was not accepted.')
        } catch (error) {
            // Already on /login. A 401 means the session itself is gone; re-probe
            // and let the page say so, rather than the global redirect.
            if (error instanceof ApiError && error.isUnauthenticated) {
                await queryClient.invalidateQueries({ queryKey: queryKeys.session })
                return
            }
            setMessage(getApiErrorMessage(error))
        } finally {
            setPending(false)
        }
    }

    return (
        <Gate
            tone="primary"
            icon={KeyRound}
            status="Verification required"
            title="Enter your two-factor code"
            actions={
                <>
                    <Button
                        type="submit"
                        form="two-factor"
                        loading={pending}
                        disabled={code.length !== 6}
                        className="w-full sm:w-auto">
                        Verify
                    </Button>
                    <CheckAgain onClick={onCheckAgain} />
                </>
            }>
            <p>
                Your session is signed in, but this browser has not been verified. Enter the 6-digit code from your
                authenticator.
            </p>

            <form
                id="two-factor"
                onSubmit={event => {
                    event.preventDefault()
                    void verify(code)
                }}>
                {/* The title and the line above already name the field; a visible label
                    would only repeat them, so it stays for screen readers only. */}
                <Label htmlFor="two-factor-code" className="sr-only">
                    Authentication code
                </Label>
                <Input
                    id="two-factor-code"
                    name="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    spellCheck={false}
                    // The page exists to collect this one value.
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    value={code}
                    aria-invalid={message ? true : undefined}
                    aria-describedby={message ? 'two-factor-error' : undefined}
                    /*
                     * Sized like the thing it is: six digits, not a text field. The
                     * trailing letter-space would push the digits left of centre, so the
                     * matching indent puts them back. `text-2xl!` overrides the
                     * coarse-pointer 16px floor in globals.css, which exists to stop iOS
                     * zooming on focus -- 24px clears that bar from the other side.
                     */
                    className="h-14 w-full indent-[0.5em] text-center font-mono text-2xl! tracking-[0.5em] pointer-coarse:h-14"
                    onChange={event => {
                        const next = event.target.value.replace(/\D/g, '').slice(0, 6)
                        setCode(next)
                        if (message) setMessage(null)
                        // The sixth digit is the entire intent, typed or pasted, so the
                        // button is the retry path rather than the normal one.
                        if (next.length === 6) void verify(next)
                    }}
                />
                {message && (
                    <p id="two-factor-error" role="alert" className="mt-2 text-13 text-destructive-soft-foreground">
                        {message}
                    </p>
                )}
            </form>

            <p className="text-13">
                You can also enter it on{' '}
                <a
                    href={env.siteUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-text underline-offset-4 hover:underline">
                    {host}
                </a>{' '}
                and then check again.
            </p>
        </Gate>
    )
}

/**
 * The four states differ only in what went wrong, so they share one object and
 * differ by tone. The tone is never the only signal: every card states its status
 * in words beside the dot, which is what keeps it readable in monochrome and for
 * colour-blind operators.
 */
type Tone = 'neutral' | 'danger' | 'warning' | 'primary'

const TONES: Record<Tone, { mark: string; dot: string; status: string }> = {
    neutral: {
        mark: 'border-border bg-surface-raised text-muted-foreground',
        dot: 'bg-muted-foreground',
        status: 'text-muted-foreground'
    },
    danger: {
        mark: 'border-destructive-soft bg-destructive-soft text-destructive-soft-foreground',
        dot: 'bg-destructive',
        status: 'text-destructive-soft-foreground'
    },
    warning: {
        mark: 'border-warning-soft bg-warning-soft text-warning-soft-foreground',
        dot: 'bg-warning',
        status: 'text-warning-soft-foreground'
    },
    primary: {
        mark: 'border-primary-soft bg-primary-soft text-primary-text',
        dot: 'bg-primary',
        status: 'text-primary-text'
    }
}

function Gate({
    tone,
    icon: Icon,
    status,
    title,
    children,
    actions
}: {
    tone: Tone
    icon: LucideIcon
    status: string
    title: string
    children: ReactNode
    actions: ReactNode
}) {
    const t = TONES[tone]

    return (
        <>
            <div className="flex flex-col gap-5 p-6 sm:p-7">
                <div className="flex items-start gap-4">
                    <span
                        aria-hidden
                        className={cn('flex size-11 shrink-0 items-center justify-center rounded-xl border', t.mark)}>
                        <Icon className="size-5" />
                    </span>
                    <div className="flex min-w-0 flex-col gap-1 pt-0.5">
                        <span
                            className={cn(
                                'flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wider',
                                t.status
                            )}>
                            <span aria-hidden className={cn('size-1.5 rounded-full', t.dot)} />
                            {status}
                        </span>
                        <h1 className="text-balance text-xl font-semibold leading-tight tracking-tight text-foreground">
                            {title}
                        </h1>
                    </div>
                </div>

                <div className="flex flex-col gap-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
            </div>

            {/* Actions sit on their own shelf so the one thing to do next is never
                buried under the explanation that led to it. */}
            <div className="flex flex-col gap-2 border-t border-border bg-surface-raised/40 p-4 sm:flex-row sm:items-center sm:px-7 sm:py-4">
                {actions}
            </div>
        </>
    )
}

/** Who the console currently sees. The first thing an operator checks. */
function Account({ email }: { email: string | undefined }) {
    return (
        <div className="flex items-center gap-2.5 rounded-lg border border-border bg-surface-raised px-3 py-2">
            <span
                aria-hidden
                className="flex size-7 shrink-0 items-center justify-center rounded-full bg-surface-hover text-xs font-medium uppercase text-muted-foreground">
                {(email ?? '?').trim().charAt(0)}
            </span>
            <span className="min-w-0 flex-1 truncate text-13 font-medium text-foreground">
                {email ?? 'this account'}
            </span>
            <span className="shrink-0 text-2xs uppercase tracking-wider text-muted-foreground">Signed in</span>
        </div>
    )
}

function Shell({ redirect, children }: { redirect?: string; children: ReactNode }) {
    return (
        <div className="flex min-h-dvh flex-col bg-background">
            <header className="flex items-center justify-between px-4 py-3 sm:px-6">
                <div className="flex items-center gap-2.5">
                    <PacksLogo aria-label="Packs" className="h-5 w-auto text-foreground" />
                    <span className="border-l border-border pl-2.5 text-13 text-muted-foreground">Admin</span>
                </div>
                <ThemeToggle />
            </header>

            <main className="flex flex-1 items-start justify-center px-4 pb-16 pt-6 sm:items-center sm:pb-20 sm:pt-0">
                <div className="w-full max-w-md">
                    {/* `overflow-hidden` so the action shelf's fill meets the card's
                        rounded corners instead of squaring them off. */}
                    <div className="card-surface overflow-hidden p-0 shadow-sm">{children}</div>

                    {redirect && redirect !== '/' && (
                        <p className="mt-4 px-1 text-xs leading-relaxed text-muted-foreground">
                            Waiting to open{' '}
                            <span className="font-id break-all text-foreground">{destinationPath(redirect)}</span>. It
                            will load as soon as this browser can see it.
                        </p>
                    )}
                </div>
            </main>
        </div>
    )
}

function CheckingSession() {
    return (
        <div
            role="status"
            aria-live="polite"
            className="flex h-dvh flex-col items-center justify-center gap-3 bg-background">
            <PacksLogo aria-hidden className="h-5 w-auto text-foreground opacity-40" />
            <span className="flex items-center gap-2 text-13 text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
                Checking your session
            </span>
        </div>
    )
}

/** Primary action. Always another origin, so never a router link. */
function Action({ href, children }: { href: string; children: ReactNode }) {
    return (
        <Button asChild className="w-full sm:w-auto">
            <a href={href} target="_blank" rel="noreferrer">
                <ExternalLink />
                {children}
            </a>
        </Button>
    )
}

function CheckAgain({ onClick }: { onClick: () => void }) {
    return (
        <Button type="button" variant="outline" onClick={onClick} className="w-full sm:w-auto">
            <RotateCw />
            Check again
        </Button>
    )
}

/** The page they were opening, without the filter string that wraps through the card. */
function destinationPath(redirect: string): string {
    const query = redirect.indexOf('?')
    const hash = redirect.indexOf('#')
    let end = redirect.length
    if (query !== -1) end = query
    if (hash !== -1 && hash < end) end = hash
    const path = redirect.slice(0, end)
    return path.startsWith('/') ? path : redirect
}

/** `https://packs.com` reads better as `packs.com` in a sentence. */
function hostname(url: string): string {
    try {
        return new URL(url).host
    } catch {
        return url
    }
}
